import {
  Box,
  Divider,
  FieldLabel,
  Flex,
  Form,
  NumericFormatField,
  Text,
} from '@/components';
import groupBy from 'lodash-es/groupBy';
import isEmpty from 'lodash-es/isEmpty';
import { type FC, Fragment, useMemo } from 'react';
import { type FieldArrayWithId, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FundBusinessFormValues } from '../../../../_model/fund-business';
import { CalculateResultOrInput } from './CalculateResultOrInput';

const MIN_YEAR_BOUNDARY = 3;

type Props = {
  fiscalYear: number;
  fields: FieldArrayWithId<FundBusinessFormValues, 'performances', 'id'>[];
  isOthers?: boolean;
};

export const FundBusinessAmountFields: FC<Props> = ({
  fiscalYear,
  fields,
  isOthers,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext<FundBusinessFormValues>();

  const minYear = fiscalYear - MIN_YEAR_BOUNDARY;

  const data = useMemo<
    {
      year: number;
      yearAmountInfos: {
        title: string | null;
        year: number;
        amountInfo?: {
          count: number | null;
          amount: number | null;
        } | null;
      }[];
    }[]
  >(() => {
    const transformFields = fields.flatMap((field) => {
      const yearAmountInfos = field.yearAmountInfos.flatMap(
        (yearAmount, yearAmountIndex) => {
          if (isEmpty(yearAmount.amountInfo)) {
            return [];
          }
          return [
            {
              ...yearAmount,
              title: field.title,
              year: minYear + yearAmountIndex,
            },
          ];
        },
      );
      return yearAmountInfos;
    });

    return Object.entries(groupBy(transformFields, 'year')).map(
      ([year, yearAmountInfos]) => ({
        year: Number(year),
        yearAmountInfos,
      }),
    );
  }, [fields, minYear]);

  return data.map(({ yearAmountInfos, year }, yearIndex) => {
    // const year = minYear + yearIndex;
    const isEstimatedYear = year >= fiscalYear;

    return (
      <Fragment key={year}>
        {yearIndex !== 0 && <Divider />}
        <Form.Item
          label={
            <FieldLabel
              text={
                isEstimatedYear
                  ? t('fund-business-estimate-amount-with-year', {
                      year,
                    })
                  : t('fund-business-amount-with-year', {
                      year,
                    })
              }
            />
          }
        >
          <Flex vertical gap={8}>
            {yearAmountInfos.flatMap((yearAmountInfo, index, arr) => {
              // 見込み額には新規出資(当初見込み)は存在しないため
              if (!isOthers && isEstimatedYear && index === 0) {
                return [];
              }
              const arrLength = arr.length;
              const isLastIndex = arrLength - 1 === index;

              return (
                <Flex key={yearAmountInfo.year} align="center">
                  <Box flex={2}>
                    <Text
                      text={isOthers ? index + 1 : yearAmountInfo.title}
                      weight="regular"
                    />
                  </Box>
                  <Box flex={2}>
                    <NumericFormatField
                      control={control}
                      name={`performances.${index}.yearAmountInfos.${yearIndex}.amountInfo.count`}
                      errors={errors}
                      addonAfter={t('number-unit')}
                      marginBottom={0}
                      allowNegative={false}
                    />
                  </Box>
                  <Box flex={5} marginLeft={8} marginRight={16}>
                    <CalculateResultOrInput
                      rowIndex={index}
                      minYear={minYear}
                      yearIndex={yearIndex}
                      name={`performances.${index}.yearAmountInfos.${yearIndex}.amountInfo.amount`}
                      readOnly={!isOthers && isLastIndex && yearIndex !== 0}
                      isOthers={isOthers}
                    />
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        </Form.Item>
      </Fragment>
    );
  });
};
